import { formatDistanceToNow, parseISO } from 'date-fns';
import {
  ArrowRight,
  CircleAlert,
  CircleCheck,
  Clock3,
  Eye,
  Layers3,
  MapPin,
  ShieldAlert,
  UserRound,
  Wrench,
} from 'lucide-react';
import { useMemo } from 'react';

import { Badge } from '@/shared/ui/badge';
import { Card, CardContent } from '@/shared/ui/card';

import { SafetyLayoutThumbnail } from './SafetyLayoutThumbnail';
import type { SafetyIssue, SafetyLayout } from './types';

interface SafetyOverviewProps {
  layouts: SafetyLayout[];
  issues: SafetyIssue[];
  onSelectLayout: (layoutId: number) => void;
  onSelectIssue: (issue: SafetyIssue) => void;
}

const AREA_NAME_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const getCreatorName = (issue: SafetyIssue) => {
  if (!issue.created_by) return 'Unknown reporter';
  const fullName = [issue.created_by.first_name, issue.created_by.last_name]
    .filter(Boolean)
    .join(' ');
  return fullName || issue.created_by.email;
};

export const SafetyOverview = ({
  layouts,
  issues,
  onSelectLayout,
  onSelectIssue,
}: SafetyOverviewProps) => {
  const layoutsById = useMemo(
    () => new Map(layouts.map((layout) => [layout.id, layout])),
    [layouts]
  );
  const recentIssues = useMemo(
    () =>
      [...issues]
        .sort(
          (left, right) =>
            parseISO(right.created_at).getTime() - parseISO(left.created_at).getTime()
        )
        .slice(0, 6),
    [issues]
  );
  const issueCountsByLayout = useMemo(() => {
    const counts = new Map<number, { open: number; resolved: number }>();
    issues.forEach((issue) => {
      if (issue.layout == null) return;
      const current = counts.get(issue.layout) ?? { open: 0, resolved: 0 };
      counts.set(issue.layout, {
        open: current.open + (issue.status === 'OPEN' ? 1 : 0),
        resolved: current.resolved + (issue.status === 'RESOLVED' ? 1 : 0),
      });
    });
    return counts;
  }, [issues]);
  const sortedLayouts = useMemo(
    () =>
      [...layouts].sort((left, right) => {
        const openCountDifference =
          (issueCountsByLayout.get(right.id)?.open ?? 0) -
          (issueCountsByLayout.get(left.id)?.open ?? 0);
        if (openCountDifference !== 0) return openCountDifference;
        return AREA_NAME_COLLATOR.compare(left.name, right.name);
      }),
    [issueCountsByLayout, layouts]
  );

  return (
    <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
      <section aria-labelledby="available-areas-heading">
        <h2
          id="available-areas-heading"
          className="mb-4 font-display text-xl font-extrabold tracking-[-0.02em]"
        >
          Available areas
        </h2>

        {layouts.length === 0 ? (
          <Card className="grid min-h-64 place-items-center border-dashed shadow-none">
            <div className="px-6 text-center">
              <Layers3 className="mx-auto mb-3 size-9 text-muted-foreground" strokeWidth={1.7} />
              <p className="font-semibold">No layouts are available</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Add a project layout before placing safety issues.
              </p>
            </div>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-3">
            {sortedLayouts.map((layout) => {
              const issueCounts = issueCountsByLayout.get(layout.id) ?? { open: 0, resolved: 0 };

              return (
                <button
                  type="button"
                  key={layout.id}
                  onClick={() => onSelectLayout(layout.id)}
                  className="group relative overflow-hidden rounded-[1.375rem] bg-card text-left shadow-slate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="relative block aspect-[16/10] overflow-hidden border-b border-border bg-muted">
                    <SafetyLayoutThumbnail layout={layout} />
                  </span>

                  <span className="flex items-center justify-between gap-4 p-5">
                    <span className="min-w-0">
                      <span className="block truncate font-display text-lg font-extrabold tracking-[-0.02em]">
                        {layout.name}
                      </span>
                      <span className="mt-2 flex items-center gap-2 text-xs font-bold">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 ${
                            issueCounts.open > 0
                              ? 'bg-destructive/10 text-destructive'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          <CircleAlert className="size-3.5 shrink-0" strokeWidth={2} />
                          {issueCounts.open} open
                        </span>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-2 py-1 text-success">
                          <CircleCheck className="size-3.5 shrink-0" strokeWidth={2} />
                          {issueCounts.resolved} resolved
                        </span>
                      </span>
                    </span>
                    <span className="grid size-9 shrink-0 place-items-center rounded-[10px] border border-border bg-muted text-muted-foreground transition-colors group-hover:border-primary group-hover:bg-primary/12 group-hover:text-primary">
                      <Eye className="size-4" strokeWidth={1.9} aria-hidden="true" />
                    </span>
                  </span>
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 z-10 rounded-[1.375rem] ring-1 ring-inset ring-border transition-colors duration-150 group-hover:ring-primary"
                  />
                </button>
              );
            })}
          </div>
        )}
      </section>

      <aside aria-labelledby="recent-safety-heading" className="xl:sticky xl:top-6">
        <h2
          id="recent-safety-heading"
          className="mb-4 font-display text-xl font-extrabold tracking-[-0.02em]"
        >
          Recent safety issues
        </h2>

        <Card className="overflow-hidden">
          {recentIssues.length === 0 ? (
            <CardContent className="grid min-h-48 place-items-center p-6 text-center">
              <div>
                <ShieldAlert
                  className="mx-auto mb-3 size-9 text-muted-foreground"
                  strokeWidth={1.7}
                />
                <p className="font-semibold">No safety issues reported yet</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Open a layout to mark the first issue.
                </p>
              </div>
            </CardContent>
          ) : (
            <div className="divide-y divide-border">
              {recentIssues.map((issue) => {
                const layout = issue.layout == null ? undefined : layoutsById.get(issue.layout);
                const canOpen = Boolean(
                  issue.layout != null && issue.position_x != null && issue.position_y != null
                );

                return (
                  <button
                    type="button"
                    key={issue.id}
                    disabled={!canOpen}
                    onClick={() => onSelectIssue(issue)}
                    className="group flex w-full cursor-pointer items-start gap-3 px-4 py-4 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-default disabled:hover:bg-transparent"
                  >
                    <span
                      className={`mt-1.5 size-2.5 shrink-0 rounded-full ${
                        issue.status === 'OPEN' ? 'bg-destructive' : 'bg-success'
                      }`}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-start justify-between gap-2">
                        <span className="line-clamp-2 font-semibold leading-5">
                          {issue.description}
                        </span>
                        <Badge variant={issue.status === 'OPEN' ? 'destructive' : 'success'}>
                          {issue.status === 'OPEN' ? 'Open' : 'Resolved'}
                        </Badge>
                      </span>
                      <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5">
                          <Layers3 className="size-3.5 shrink-0" />
                          {layout?.name ?? 'No layout'}
                        </span>
                        {issue.location_detail?.name && (
                          <span className="inline-flex items-center gap-1.5">
                            <MapPin className="size-3.5 shrink-0" />
                            {issue.location_detail.name}
                          </span>
                        )}
                        {issue.trade_detail?.name && (
                          <span className="inline-flex items-center gap-1.5">
                            <Wrench className="size-3.5 shrink-0" />
                            {issue.trade_detail.name}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1.5">
                          <UserRound className="size-3.5 shrink-0" />
                          {getCreatorName(issue)}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <Clock3 className="size-3.5 shrink-0" />
                          {formatDistanceToNow(parseISO(issue.created_at), { addSuffix: true })}
                        </span>
                      </span>
                    </span>
                    {canOpen && (
                      <ArrowRight className="mt-1 size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </Card>
      </aside>
    </div>
  );
};
